/**
 * Highlights every case-insensitive match of `text` inside `target` using the
 * CSS Custom Highlight API (`CSS.highlights`). All previous highlights registered
 * under this function's named highlight set are cleared before each new pass so
 * stale marks never accumulate.
 *
 * Styling is applied via the `::highlight(client-search-string-results)` CSS
 * pseudo-element — no DOM mutations occur; only `Range` objects are registered.
 *
 * No-ops when `target` is null, `text` is empty, or the browser does not
 * support `CSS.highlights`.
 *
 * @param text   - The search string to highlight (case-insensitive).
 * @param target - The container element to scope the highlight pass to.
 */
export function highlightSearchText(text: string, target: HTMLElement | null) {
  if (CSS.highlights) {
    // Named highlight set — cleared on every call so the previous query's
    // marks are removed before the new ranges are registered.
    const name = "client-search-string-results";
    CSS.highlights.delete(name);
    const highlights: Range[] = [];

    /**
     * Finds every case-insensitive occurrence of `text` within `textNode`
     * and pushes a `Range` for each match into the outer `highlights` array.
     * Uses a running `startPos` index to iterate through all occurrences in a
     * single pass without mutating the DOM.
     *
     * @param textNode - The DOM text node to search within.
     * @param highlightNodeString - The search string (case-insensitive).
     */
    const highlightNodeText = (
      textNode: Text,
      highlightNodeString: string
    ): void => {
      const lowerCase = highlightNodeString.toLowerCase();
      if (lowerCase.length === 0) return;

      // `startPos` slides forward by the length of each match so all occurrences
      // are found in a single left-to-right pass. The loop exits when
      // `indexOf` can no longer find the search string in the remaining text.
      const current: Text = textNode;
      const value = current.nodeValue?.toLowerCase() ?? "";
      let startPos = 0;
      while ((startPos = value?.indexOf(lowerCase, startPos)) !== -1) {
        const range = new Range();
        range.setStart(current, startPos);
        range.setEnd(current, startPos + lowerCase.length);
        startPos += lowerCase.length;
        highlights.push(range);
      }
    };

    if (target) {
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
        acceptNode(node: Node): number {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          // Skip non-visible embedded content.
          if (["SCRIPT", "STYLE", "NOSCRIPT"].includes(parent.tagName)) {
            return NodeFilter.FILTER_REJECT;
          }
          // Skip whitespace-only nodes — nothing to highlight.
          if ((node.nodeValue ?? "").trim() === "")
            return NodeFilter.FILTER_SKIP;
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      // Collect all text nodes before starting the range pass. This separates
      // the tree walk from the range-building loop for clarity.
      const textNodes: Text[] = [];
      let node: Node | null;
      while ((node = walker.nextNode())) {
        textNodes.push(node as Text);
      }

      for (const textNode of textNodes) {
        highlightNodeText(textNode, text);
      }
      // Register the Highlight object
      CSS.highlights.set(name, new Highlight(...highlights));
    }
  }
}
